import { useState, type FormEvent } from "react";
import { useProcedure } from "highscale/react";
import { at } from "./lexicons";
import { RoundPlanFields, useRoundPlan } from "./RoundPlanFields";
import { localStart } from "./round-planning";
type Plan = at.tastetest.play.state.PlannedRound;
export function FutureRoundsEditor({
  league,
  roundNumber,
  votingEndsAt,
  plans,
  testType,
  onClose,
}: {
  league: string;
  roundNumber: number;
  votingEndsAt: number;
  plans: Plan[];
  testType: string;
  onClose: () => void;
}) {
  const [snapshot] = useState(() => ({
    plans: plans.map((p) => ({ ...p })),
    roundNumber,
    votingEndsAt,
  }));
  const first = snapshot.plans[0];
  const calendarDays = (end: number, start: number) => {
    const day = (time: number) => {
      const d = new Date(time);
      return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
    };
    return Math.min(
      14,
      Math.max(1, Math.round((day(end) - day(start)) / 86400000)),
    );
  };
  const days = first ? calendarDays(first.submissionEndsAt, first.startsAt) : 5;
  const due = new Date(
    first?.submissionEndsAt ?? Math.max(snapshot.votingEndsAt, Date.now()),
  );
  if (!first) due.setDate(due.getDate() + days);
  const planner = useRoundPlan({
    drafts: snapshot.plans.map((p) => ({
      theme: p.theme,
      description: p.description ?? "",
    })),
    deadline: localStart(due),
    submissionDays: days,
    votingDays: first
      ? calendarDays(first.votingEndsAt, first.submissionEndsAt)
      : 2,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const edit = useProcedure(at.tastetest.play.edit, {
    invalidates: [at.tastetest.play.get],
  });
  async function save(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const plannedRounds = planner
        .plans()
        .map((p, i) =>
          i === 0 ? { ...p, startsAt: snapshot.votingEndsAt } : p,
        );
      if (
        plannedRounds.length !== planner.drafts.length ||
        plannedRounds.some(
          (p) => !p.theme.trim() || p.submissionEndsAt <= p.startsAt,
        )
      )
        throw new Error(
          "Give each future round a theme and a valid deadline after the current round.",
        );
      const response = await edit({
        league,
        expectedRound: snapshot.roundNumber,
        expectedPlans: snapshot.plans,
        plannedRounds,
      });
      if (!response.ok) {
        setError(response.message ?? "Could not save future rounds.");
        return;
      }
      onClose();
    } catch (failure) {
      setError(
        failure instanceof Error && failure.message.startsWith("Give each")
          ? failure.message
          : "Could not save. Your edits are still here; try again.",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={(e) => void save(e)} aria-label="Edit league">
      <h3>Edit league</h3>
      <p className="muted">
        Changes apply to future rounds. Round {snapshot.roundNumber} and
        completed rounds stay as they are.
      </p>
      <RoundPlanFields
        plan={planner}
        testType={testType}
        busy={busy}
        offset={snapshot.roundNumber}
        anchor={snapshot.votingEndsAt}
      />
      {!!planner.drafts.length && (
        <button
          type="button"
          className="secondary"
          disabled={busy}
          onClick={() => planner.setDrafts([])}
        >
          Remove all future rounds
        </button>
      )}
      {error && <p role="alert">{error}</p>}
      <div className="round-editor-actions">
        <button disabled={busy}>{busy ? "Saving…" : "Save changes"}</button>
        <button
          type="button"
          className="secondary"
          disabled={busy}
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
